import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, PrimaryButton, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import { useMakeRequestHook } from '../requestUtils';

export interface AutodeskCurrencyRateEditorProps {
  eurToNokRate?: number;
  onSaved: () => void;
}

/**
 * Split out so a keystroke here doesn't re-render AutodeskCatalog. BaseTable
 * hands column defs straight to AgGridReact without memoising, and a rebuild
 * landing mid-drag on a column resize throws on a null classList.
 */
export function AutodeskCurrencyRateEditor(props: AutodeskCurrencyRateEditorProps) {
  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const makeRequest = useMakeRequestHook();
  const [rateInput, setRateInput] = useState(props.eurToNokRate != null ? String(props.eurToNokRate) : '');
  const [savingRate, setSavingRate] = useState(false);

  useEffect(() => {
    setRateInput(props.eurToNokRate != null ? String(props.eurToNokRate) : '');
  }, [props.eurToNokRate]);

  const saveRate = async () => {
    const parsed = Number(rateInput.replace(',', '.'));
    if (!rateInput || Number.isNaN(parsed) || parsed <= 0) {
      setSnackbarMessage({ content: t("autodeskCatalog.invalidRate"), type: "error" });
      return;
    }
    setSavingRate(true);
    try {
      await makeRequest('autodesk/currency', 'PUT', { eurToNokRate: parsed });
      setSnackbarMessage({ content: t("autodeskCatalog.rateSaved"), type: "success" });
      props.onSaved();
    } finally {
      setSavingRate(false);
    }
  };

  return (
    <Box className="d-flex align-items-center gap-2 mb-3">
      <span>{t("autodeskCatalog.eurToNokRate")}:</span>
      <TextField
        size="small"
        placeholder={`${t("autodeskCatalog.notSet")}`}
        value={rateInput}
        onChange={(e: any) => setRateInput(e.target.value)}
        sx={{ width: '100px' }}
      />
      <PrimaryButton
        variant="outlined"
        size="small"
        disabled={savingRate}
        onClick={saveRate}
      >
        {t("autodeskCatalog.saveRate")}
      </PrimaryButton>
    </Box>
  );
}

export default AutodeskCurrencyRateEditor;
